import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import type {
  ArtworkController,
  ArtworkMode,
  ArtworkModeChangeResult,
} from "./artwork-controller";
import type {
  ConfiguratorController,
  ConfiguratorField,
  ConfiguratorSchema,
} from "./configurator-controller";
import { FIELD_DOM_ORDER } from "./configurator-controller";
import type { DraftController } from "./draft-controller";
import type {
  CartSnapshot,
  CartSummary,
  OrderLineController,
  OrderLineState,
} from "./order-line-controller";
import type { PricingController, PricePreview } from "./pricing-controller";
import {
  addIsraeliProductionDays,
  PVC_STANDARD_PRODUCTION_DAYS,
  todayInIsrael,
} from "./production-calendar";
import type {
  ProductPageController,
  SubmitBlockedReason,
  SubmitOutcome,
} from "./product-page-controller";
import {
  artworkStatusMessage,
  buildPriceDisplay,
  conflictMessage,
  displayedFieldError,
  formatDate,
  formatMoney,
  isOrderBusy,
  localAreaSqm,
  submitLabel,
  type PvcProductPresentation,
} from "./pvc-product-page-view-model";

export type DateFieldRenderProps = {
  id: string;
  value: string | null;
  min?: string;
  ariaInvalid: boolean;
  ariaLabelledBy: string;
  describedBy: string;
  onChange(value: string | null): void;
  onBlur(): void;
};

export type PvcProductPageProps = {
  page: ProductPageController;
  configurator: ConfiguratorController;
  pricing: PricingController;
  artwork: ArtworkController;
  orderLine: OrderLineController;
  draft: DraftController;
  schema: ConfiguratorSchema;
  product: PvcProductPresentation;
  mode?: "create" | "edit";
  /** Slot for the selected Date Picker adapter. A native date input is the safe fallback. */
  renderDateField?: (props: DateFieldRenderProps) => ReactNode;
  artworkDesigner?: ReactNode;
  onCartSnapshot?(cart: CartSnapshot): void;
  onOpenMiniCart?(): void;
  onBusinessQuote?(): void;
  onRequestProductRefresh?(): void;
  onRequestOrderLineReload?(): void;
};

type ModeConfirmation = Extract<ArtworkModeChangeResult, { type: "confirmation-required" }>;

type DialogState =
  | { type: "mode"; value: ModeConfirmation }
  | { type: "remove-file" }
  | null;

const FIELD_IDS: Record<ConfiguratorField, string> = {
  widthCm: "pvc-width",
  heightCm: "pvc-height",
  quantity: "pvc-quantity",
  materialId: "pvc-material-group",
  addonIds: "pvc-addon-group",
  requestedDate: "pvc-requested-date",
};

const PRODUCT_TRUST_ITEMS = [
  { title: "מחיר חי", description: "המחיר מתעדכן לפי מידה, חומר וכמות." },
  { title: "ייצור מקומי", description: "העבודה נכנסת למסלול ייצור ברור." },
  { title: "קובץ או עיצוב", description: "אפשר להעלות קובץ או לבקש עיצוב." },
  { title: "איסוף עצמי", description: "אפשר לתאם איסוף עצמי. אפשרויות אספקה נוספות יוצגו בהזמנה כשהן זמינות." },
] as const;

const PVC_SIZE_PRESETS = [
  { label: "קטן", width: 30, height: 20 },
  { label: "סטנדרטי", width: 60, height: 40 },
  { label: "בינוני", width: 70, height: 50 },
  { label: "גדול", width: 100, height: 70 },
  { label: "רחב", width: 120, height: 80 },
] as const;

function useControllerState<T>(controller: {
  getState(): T;
  subscribe(listener: (state: T) => void): () => void;
}): T {
  return useSyncExternalStore(
    controller.subscribe,
    controller.getState,
    controller.getState,
  );
}

function focusById(id: string) {
  requestAnimationFrame(() => {
    const element = document.getElementById(id);
    if (!element) return;
    // Do not move focus behind an active modal/sheet. The blocking surface
    // marks the page subtree inert while it owns focus.
    if (element.closest("[inert], [aria-hidden='true']")) return;
    element.focus();
  });
}

function focusBlocked(reason: SubmitBlockedReason) {
  if (reason.kind === "configuration") {
    if (reason.errorFields.length >= 2) {
      focusById("pvc-config-error-summary");
      return;
    }
    const first = reason.errorFields[0];
    if (first) focusById(FIELD_IDS[first]);
    return;
  }

  if (reason.kind === "artwork") {
    focusById("pvc-artwork-section");
    return;
  }

  if (reason.kind === "quote" || reason.kind === "price-changed") {
    focusById("pvc-price-summary");
  }
}

function useTransientDraftNotice(saveNoticeRevision: number): boolean {
  const [visible, setVisible] = useState(false);
  const previous = useRef(saveNoticeRevision);

  useEffect(() => {
    if (saveNoticeRevision === previous.current) return;
    previous.current = saveNoticeRevision;
    setVisible(true);
    const id = globalThis.setTimeout(() => setVisible(false), 2_000);
    return () => globalThis.clearTimeout(id);
  }, [saveNoticeRevision]);

  return visible;
}

function AccessibleSurface(props: {
  type: "modal" | "sheet";
  title: string;
  open: boolean;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
}): ReactNode {
  const { type, title, open, onClose, children, footer, dismissible = true } = props;
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const titleId = type === "modal" ? "pvc-modal-title" : "pvc-sheet-title";
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const backgrounds = [...document.querySelectorAll<HTMLElement>("[data-pvc-page-background]")];
    const previousInert = backgrounds.map(element => [element, element.inert] as const);
    for (const element of backgrounds) element.inert = true;

    const surface = surfaceRef.current;
    requestAnimationFrame(() => {
      const first = surface?.querySelector<HTMLElement>(
        "button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex='-1'])",
      );
      (first ?? surface)?.focus();
    });

    return () => {
      for (const [element, wasInert] of previousInert) element.inert = wasInert;
      returnFocusRef.current?.focus();
    };
  }, [open]);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && dismissible) {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;

    const root = surfaceRef.current;
    if (!root) return;
    const focusables = [...root.querySelectorAll<HTMLElement>(
      "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
    )];
    if (focusables.length === 0) {
      event.preventDefault();
      root.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, [onClose, dismissible]);

  if (!open || typeof document === "undefined") return null;

  const content = type === "modal" ? (
    <div className="overlay overlay--centered">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={surfaceRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="modal__header">
          <h2 className="modal__title" id={titleId}>{title}</h2>
          {dismissible ? <button className="modal__close" type="button" onClick={onClose} aria-label="סגירה">×</button> : null}
        </div>
        <div className="modal__body">{children}</div>
        {footer ? <div className="modal__footer">{footer}</div> : null}
      </div>
    </div>
  ) : (
    <>
      <div className="overlay" aria-hidden="true" onClick={onClose} />
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-state="open"
        ref={surfaceRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="sheet__handle" aria-hidden="true" />
        <div className="sheet__header">
          <h2 className="sheet__title" id={titleId}>{title}</h2>
          {dismissible ? <button className="sheet__close" type="button" onClick={onClose} aria-label="סגירה">×</button> : null}
        </div>
        <div className="sheet__body">{children}</div>
        {footer ? <div className="pvc-sheet-footer">{footer}</div> : null}
      </div>
    </>
  );

  return createPortal(content, document.body);
}

function FieldErrorText(props: { id: string; message: string | null }) {
  if (!props.message) return null;
  return <p className="pvc-field-error" id={props.id}>{props.message}</p>;
}

function PriceDetails(props: { quote: PricePreview; rushSelected: boolean }) {
  const { quote } = props;
  const readyDate = formatDate(quote.productionEstimate.earliestReadyDate);

  return (
    <div className="pvc-price-details">
      <dl className="pvc-summary-list">
        <div><dt>מחיר ליחידה</dt><dd><bdi>{formatMoney(quote.unitPriceMinor, quote.currencyCode)}</bdi></dd></div>
        {quote.pricePerSqmMinor !== null ? (
          <div><dt>מחיר למ״ר</dt><dd><bdi>{formatMoney(quote.pricePerSqmMinor, quote.currencyCode)}</bdi></dd></div>
        ) : null}
        <div><dt>לפני מע״מ</dt><dd><bdi>{formatMoney(quote.subtotalMinor, quote.currencyCode)}</bdi></dd></div>
        <div><dt>מע״מ</dt><dd><bdi>{formatMoney(quote.taxMinor, quote.currencyCode)}</bdi></dd></div>
      </dl>
      {quote.minChargeApplied ? (
        <p className="pvc-info-note">
          מחיר מינימום להזמנה זו: <bdi>{formatMoney(quote.minimumChargeMinor, quote.currencyCode)}</bdi>
        </p>
      ) : null}
      {readyDate ? <p className="pvc-production-estimate">מוכן לאיסוף בערך ב־<bdi>{readyDate}</bdi></p> : null}
      {props.rushSelected && quote.productionEstimate.rush?.available ? (
        <p className="pvc-production-estimate pvc-production-estimate--rush">
          ייצור דחוף — התוספת כלולה במחיר:
          {quote.productionEstimate.rush.surchargeMinor !== null
            ? <> בתוספת <bdi>{formatMoney(quote.productionEstimate.rush.surchargeMinor, quote.currencyCode)}</bdi></>
            : null}
        </p>
      ) : null}
    </div>
  );
}

function PriceValue(props: {
  pricingState: ReturnType<PricingController["getState"]>;
  compact?: boolean;
  onRetry(): void;
}) {
  const model = buildPriceDisplay(props.pricingState);

  if (model.kind === "skeleton") {
    return <span className="skeleton skeleton--price" aria-hidden="true" />;
  }

  if (model.kind === "none") {
    return model.message ? <span className="pvc-price-message">{model.message}</span> : <span>—</span>;
  }

  const className = model.recalculating
    ? "price--recalculating"
    : model.stale
      ? "price--stale"
      : undefined;

  return (
    <div className={className}>
      <strong className={props.compact ? "pvc-price pvc-price--compact" : "pvc-price"}>
        <bdi>{formatMoney(model.quote.totalMinor, model.quote.currencyCode)}</bdi>
      </strong>
      <span className="pvc-vat-label">{model.quote.includesVat ? "כולל מע״מ" : "לפני מע״מ"}</span>
      {model.stale ? (
        <div className="pvc-price-recovery">
          <span>{model.staleMessage}</span>
          {props.compact ? null : (
            <button className="pvc-link-button" type="button" onClick={props.onRetry}>לנסות שוב</button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function ProductGallery(props: {
  product: PvcProductPresentation;
  width: number | null;
  height: number | null;
}) {
  const items = props.product.gallery ?? [];
  const [selectedId, setSelectedId] = useState(items[0]?.id ?? null);
  const selected = items.find(item => item.id === selectedId) ?? items[0] ?? null;
  const ratio = props.width && props.height && props.width > 0 && props.height > 0
    ? `${props.width} / ${props.height}`
    : "4 / 3";

  useEffect(() => {
    if (selectedId && items.some(item => item.id === selectedId)) return;
    setSelectedId(items[0]?.id ?? null);
  }, [items, selectedId]);

  return (
    <section className="pvc-gallery" aria-label="תצוגת המוצר">
      <div className="gallery__viewer" aria-live="polite">
        {selected ? (
          <img className="gallery__image" src={selected.src} alt={selected.alt} />
        ) : (
          <div className="pvc-preview-stage" aria-label="המחשה של השלט">
            <div className="pvc-preview-sign" style={{ aspectRatio: ratio }}>
              <span>השלט שלכם</span>
            </div>
          </div>
        )}
      </div>
      <p className="gallery__disclaimer">המחשה בלבד</p>
      {items.length > 1 ? (
        <div className="gallery__thumbs" aria-label="בחירת תמונת מוצר">
          {items.map(item => (
            <div className="gallery__thumb-item" key={item.id}>
              <button
                type="button"
                className="gallery__thumb"
                aria-pressed={selected?.id === item.id}
                onClick={() => setSelectedId(item.id)}
              >
                <img src={item.src} alt="" />
              </button>
              {item.label ? <span className="gallery__thumb-label">{item.label}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function CartChoice(props: {
  guestCart: CartSummary;
  accountCart: CartSummary;
  busy: boolean;
  onChoose(choice: "guest-cart" | "account-cart"): void;
}) {
  const row = (title: string, cart: CartSummary, choice: "guest-cart" | "account-cart") => (
    <button
      type="button"
      className="pvc-cart-choice"
      disabled={props.busy}
      onClick={() => props.onChoose(choice)}
    >
      <strong>{title}</strong>
      <span>{cart.totalQuantity} פריטים</span>
      <bdi>{formatMoney(cart.totalWithTax, cart.currencyCode)}</bdi>
    </button>
  );

  return (
    <div className="pvc-cart-choices">
      {row("הסל הנוכחי", props.guestCart, "guest-cart")}
      {row("הסל שבחשבון", props.accountCart, "account-cart")}
      <p className="pvc-muted">לא מתבצע מיזוג אוטומטי ואף סל לא נמחק.</p>
    </div>
  );
}

export function PvcProductPage(props: PvcProductPageProps) {
  const {
    page,
    configurator,
    pricing,
    artwork,
    orderLine,
    draft,
    schema,
    product,
    mode = "create",
  } = props;

  const configState = useControllerState(configurator);
  const pricingState = useControllerState(pricing);
  const artworkState = useControllerState(artwork);
  const orderState = useControllerState(orderLine);
  const draftState = useControllerState(draft);

  const [dialog, setDialog] = useState<DialogState>(null);
  const [priceSheetOpen, setPriceSheetOpen] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<ConfiguratorField[]>([]);
  const [liveMessage, setLiveMessage] = useState("");
  const [initMessage, setInitMessage] = useState<string | null>(null);
  const initPromiseRef = useRef<Promise<unknown> | null>(null);
  const lastSuccessMutationRef = useRef<string | null>(null);
  const lastAnnouncedPriceChangeKeyRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const draftNotice = useTransientDraftNotice(draftState.saveNoticeRevision);

  const constraints = schema.constraints;
  const priceDisplay = buildPriceDisplay(pricingState);
  const quote = priceDisplay.kind === "value" ? priceDisplay.quote : null;

  const widthError = displayedFieldError(configState, "widthCm", constraints);
  const heightError = displayedFieldError(configState, "heightCm", constraints);
  const quantityError = displayedFieldError(configState, "quantity", constraints);
  const materialError = displayedFieldError(configState, "materialId", constraints);
  const addonError = displayedFieldError(configState, "addonIds", constraints);
  const dateError = displayedFieldError(configState, "requestedDate", constraints);

  const area = quote?.areaPerUnitSqm ?? localAreaSqm(configState);
  const totalArea = quote?.totalAreaSqm ?? (
    area !== null && configState.quantity.value !== null
      ? area * configState.quantity.value
      : null
  );
  const availableSizePresets = PVC_SIZE_PRESETS.filter(
    (preset) =>
      preset.width >= constraints.minWidthCm &&
      preset.width <= constraints.maxWidthCm &&
      preset.height >= constraints.minHeightCm &&
      preset.height <= constraints.maxHeightCm,
  );

  const materialById = useMemo(
    () => new Map(product.materials.map(item => [item.id, item])),
    [product.materials],
  );
  const addonById = useMemo(
    () => new Map(product.addons.map(item => [item.id, item])),
    [product.addons],
  );

  useEffect(() => {
    if (!initPromiseRef.current) {
      initPromiseRef.current = page.init().then(outcome => {
        if (outcome.draft === "restored") setInitMessage("הטיוטה מהמכשיר שוחזרה.");
        if (outcome.draft === "discarded") setInitMessage("טיוטה ישנה לא התאימה לגרסה הנוכחית ולכן לא שוחזרה.");
        if (outcome.removedAddonIds.length > 0) {
          setInitMessage("חלק מהאפשרויות בטיוטה כבר אינן זמינות והוסרו.");
        } else if (outcome.materialInvalidated) {
          setInitMessage("החומר שהיה בטיוטה אינו זמין יותר. יש לבחור חומר מחדש.");
        }
        return outcome;
      }).catch(() => {
        setInitMessage("לא הצלחנו לשחזר את כל נתוני הטיוטה. אפשר להמשיך ולהגדיר את המוצר.");
      });
    }
  }, [page]);

  useEffect(() => {
    if (orderState.status !== "success") return;
    const mutationId = orderState.result.clientMutationId;
    if (lastSuccessMutationRef.current === mutationId) return;
    lastSuccessMutationRef.current = mutationId;
    setSubmitErrors([]);
    props.onCartSnapshot?.(orderState.result.cart);
    props.onOpenMiniCart?.();
    setLiveMessage(orderState.operation === "update" ? "הפריט עודכן בסל." : "הפריט נוסף לסל.");
  }, [orderState, props]);

  useEffect(() => {
    if (pricingState.status !== "ready") return;
    if (orderState.status !== "conflict" || orderState.conflict.code !== "PRICE_CHANGED") return;

    // PRICE_CHANGED has exactly one polite announcement channel. The visible
    // conflict notice intentionally has no live role; this region announces the
    // replacement quote once, keyed to the exact server quote.
    const key = `${pricingState.quote.configurationHash}:${pricingState.quote.pricingVersion}`;
    if (lastAnnouncedPriceChangeKeyRef.current === key) return;
    lastAnnouncedPriceChangeKeyRef.current = key;
    setLiveMessage(`המחיר עודכן ל־${formatMoney(pricingState.quote.totalMinor, pricingState.quote.currencyCode)}. יש לאשר שוב.`);
  }, [pricingState, orderState]);

  const handleSubmitOutcome = useCallback((outcome: SubmitOutcome) => {
    if (outcome.type === "blocked") {
      if (outcome.reason.kind === "configuration") setSubmitErrors(outcome.reason.errorFields);
      focusBlocked(outcome.reason);
      if (outcome.reason.kind === "artwork") setLiveMessage("יש להשלים את בחירת הקובץ לפני ההוספה לסל.");
      if (outcome.reason.kind === "quote") setLiveMessage("צריך מחיר עדכני לפני ההוספה לסל.");
      // PRICE_CHANGED is announced only after the replacement quote arrives;
      // announcing here as well would duplicate the same price update for SR users.
      return;
    }
    if (outcome.type === "conflict") {
      if (outcome.conflict.code !== "PRICE_CHANGED") {
        setLiveMessage(conflictMessage(outcome.conflict));
      }
      return;
    }
    if (outcome.type === "unknown-outcome" || outcome.type === "pending") {
      setLiveMessage("בודקים אם הפריט נוסף לסל. לא שולחים שוב את ההזמנה בינתיים.");
      return;
    }
    if (outcome.type === "failed") {
      setLiveMessage("לא הצלחנו להשלים את הפעולה. אפשר לנסות שוב.");
    }
  }, []);

  const onSubmit = useCallback(async () => {
    // This acknowledgement is intentionally driven by the RENDERED conflict
    // state. A queued second click from the old UI cannot silently acknowledge
    // a replacement price before the customer has seen the new CTA/price.
    if (orderState.status === "conflict" && orderState.conflict.code === "PRICE_CHANGED") {
      if (!page.acknowledgePriceChange()) {
        setLiveMessage("המחיר החדש עדיין לא מוכן לאישור.");
        return;
      }
    }
    const outcome = await page.submit();
    handleSubmitOutcome(outcome);
  }, [page, handleSubmitOutcome, orderState]);

  const chooseArtworkMode = useCallback(async (next: ArtworkMode) => {
    const result = await page.onArtworkModeChange(next);
    if (result.type === "confirmation-required") {
      setDialog({ type: "mode", value: result });
    } else if (result.type === "rejected") {
      setLiveMessage("אפשרות הקובץ הזו אינה זמינה עבור המוצר.");
    }
  }, [page]);

  const confirmDialog = useCallback(async () => {
    if (!dialog) return;
    if (dialog.type === "mode") {
      await page.onArtworkModeConfirm(dialog.value.requestedMode);
      setDialog(null);
      return;
    }
    if (dialog.type === "remove-file") {
      await page.onArtworkRemove();
      setDialog(null);
      if (artwork.getState().file === null) {
        setLiveMessage("הקובץ הוסר מההזמנה.");
      } else {
        setLiveMessage("לא הצלחנו להסיר את הקובץ. הוא נשאר משויך להזמנה.");
      }
    }
  }, [dialog, page]);

  const onFileSelected = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) return;
    await page.onArtworkUpload(file);
  }, [page]);

  const onResolveCart = useCallback(async (choice: "guest-cart" | "account-cart") => {
    await page.resolveCartOwnership(choice);
    setLiveMessage("הסל נבחר. המחיר מחושב מחדש לפני המשך ההזמנה.");
  }, [page]);

  const applySizePreset = useCallback(async (preset: typeof PVC_SIZE_PRESETS[number]) => {
    page.onDimensionInput("widthCm", String(preset.width));
    page.onDimensionInput("heightCm", String(preset.height));
    await page.onDimensionBlur("widthCm");
    await page.onDimensionBlur("heightCm");
    setLiveMessage(`עודכנה מידה ${preset.width}×${preset.height} ס״מ.`);
  }, [page]);

  const cartOwnership = orderState.status === "conflict" && orderState.conflict.code === "CART_OWNERSHIP_CONFLICT"
    ? orderState.conflict
    : null;

  const orderBusy = isOrderBusy(orderState) || orderState.status === "unknown-outcome";
  const quoteBusy = pricingState.status === "initial-loading" || pricingState.status === "recalculating";
  const quoteUnavailable = pricingState.status === "stale" || pricingState.status === "error" || pricingState.status === "rejected";
  const ownershipBlocked = Boolean(cartOwnership);

  const currentConflict = orderState.status === "conflict" ? orderState.conflict : null;
  const priceChangeConflict = currentConflict?.code === "PRICE_CHANGED";
  const priceChangeNeedsRetry = Boolean(
    priceChangeConflict && (pricingState.status === "stale" || pricingState.status === "error"),
  );
  const artworkMessage = artworkStatusMessage(artworkState);

  const renderOrderConflictAction = () => {
    if (!currentConflict) return null;
    switch (currentConflict.code) {
      case "CART_SESSION_EXPIRED":
        return <button className="pvc-button pvc-button--secondary" type="button" onClick={() => void page.recoverCartSession()}>שחזור הסל</button>;
      case "PRODUCT_SCHEMA_CHANGED":
        return props.onRequestProductRefresh ? <button className="pvc-button pvc-button--secondary" type="button" onClick={props.onRequestProductRefresh}>רענון אפשרויות המוצר</button> : null;
      case "ORDER_LINE_VERSION_CONFLICT":
        return props.onRequestOrderLineReload ? <button className="pvc-button pvc-button--secondary" type="button" onClick={props.onRequestOrderLineReload}>טעינת הפריט העדכני</button> : null;
      default:
        return null;
    }
  };

  const summaryButtonLabel = submitLabel(orderState, mode);

  const retryPriceChange = useCallback(async () => {
    const outcome = await page.retryPricing();
    if (outcome.type === "failed") {
      setLiveMessage("לא הצלחנו לחשב את המחיר החדש. אפשר לנסות שוב.");
    }
  }, [page]);

  const primaryActionLabel = priceChangeConflict
    ? pricingState.status === "ready"
      ? "להוסיף לסל במחיר החדש"
      : pricingState.status === "initial-loading" || pricingState.status === "recalculating"
        ? "מעדכנים מחיר…"
        : priceChangeNeedsRetry
          ? "לנסות לחשב מחיר מחדש"
          : "יש לעדכן את ההגדרות"
    : summaryButtonLabel;

  const primaryActionDisabled = priceChangeConflict
    ? orderBusy || ownershipBlocked || (
        pricingState.status !== "ready" && !priceChangeNeedsRetry
      )
    : orderBusy || quoteBusy || quoteUnavailable || ownershipBlocked;

  const onPrimaryAction = priceChangeNeedsRetry
    ? retryPriceChange
    : onSubmit;

  return (
    <>
      <a className="pvc-page-skip-link" href="#product-configurator">דלגו להגדרת המוצר</a>

      <div className="pvc-product-page has-sticky-bar" dir="rtl" data-pvc-page-root data-pvc-page-background>
        <nav className="breadcrumbs pvc-breadcrumbs" aria-label="פירורי לחם">
          <ol className="breadcrumbs__list">
            <li className="breadcrumbs__item"><a className="breadcrumbs__link" href="/">דף הבית</a></li>
            <li className="breadcrumbs__item" aria-hidden="true"><span className="breadcrumbs__separator icon-mirror">›</span></li>
            <li className="breadcrumbs__item breadcrumbs__current" aria-current="page">{product.title}</li>
          </ol>
        </nav>

        <header className="pvc-product-heading">
          <div>
            <span className="badge badge--brand">הדפסה לפי מידה</span>
            <h1>{product.title}</h1>
            {product.description ? <p>{product.description}</p> : null}
          </div>
          {draftNotice ? <span className="pvc-save-notice" aria-live="polite">✓ נשמר במכשיר זה</span> : null}
        </header>

        <section className="pvc-product-trust" aria-label="יתרונות הזמנה באתר">
          {PRODUCT_TRUST_ITEMS.map(item => (
            <div className="pvc-product-trust__item" key={item.title}>
              <strong>{item.title}</strong>
              <span>{item.description}</span>
            </div>
          ))}
        </section>

        {initMessage ? <div className="pvc-notice pvc-notice--info">{initMessage}</div> : null}

        {currentConflict && currentConflict.code !== "CART_OWNERSHIP_CONFLICT" ? (
          <div
            className="pvc-notice pvc-notice--error pvc-global-order-feedback"
            role={currentConflict.code === "PRICE_CHANGED" ? undefined : "alert"}
          >
            <p>{conflictMessage(currentConflict)}</p>
            {renderOrderConflictAction()}
          </div>
        ) : null}

        {orderState.status === "unknown-outcome" ? (
          <div className="pvc-notice pvc-notice--warning pvc-global-order-feedback">
            <p>בודקים אם הפעולה כבר בוצעה כדי למנוע פריט כפול.</p>
            <div className="pvc-inline-actions">
              <button className="pvc-button pvc-button--secondary" type="button" onClick={() => void page.resolveUnknownOrderOutcome()}>בדיקה עכשיו</button>
              {orderState.canRetry ? <button className="pvc-button pvc-button--secondary" type="button" onClick={() => void page.retryUnknownOrderOutcome()}>ניסיון חוזר בטוח</button> : null}
            </div>
          </div>
        ) : null}

        <div className="pvc-product-layout">
          <div className="pvc-layout-gallery">
            <ProductGallery product={product} width={configState.width.value} height={configState.height.value} />
          </div>

          <main className="pvc-layout-configurator" id="product-configurator" tabIndex={-1}>
            <section className="pvc-configurator-card" aria-labelledby="pvc-config-title">
              <div className="pvc-section-heading">
                <div>
                  <span className="pvc-step-number" aria-hidden="true">1</span>
                  <h2 id="pvc-config-title">הגדרת השלט</h2>
                </div>
                <p>המחיר מתעדכן אוטומטית לפי ההגדרות.</p>
              </div>

              {submitErrors.length >= 2 ? (
                <div
                  className="pvc-error-summary"
                  id="pvc-config-error-summary"
                  role="alert"
                  tabIndex={-1}
                >
                  <strong>יש כמה פרטים שצריך להשלים לפני ההוספה לסל:</strong>
                  <ul>
                    {FIELD_DOM_ORDER.filter(field => submitErrors.includes(field)).map(field => (
                      <li key={field}><a href={`#${FIELD_IDS[field]}`}>{displayedFieldError(configState, field, constraints) ?? "יש לבדוק את השדה."}</a></li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <fieldset className="pvc-section" aria-describedby="pvc-size-help">
                <legend>מידה</legend>
                <p className="pvc-help" id="pvc-size-help">
                  הזינו מידות בס״מ. טווח רוחב: <bdi>{constraints.minWidthCm}–{constraints.maxWidthCm}</bdi>, טווח גובה: <bdi>{constraints.minHeightCm}–{constraints.maxHeightCm}</bdi>.
                </p>
                <div className="pvc-field-grid pvc-field-grid--two">
                  <label className="pvc-field" htmlFor="pvc-width">
                    <span>רוחב בס״מ</span>
                    <input
                      id="pvc-width"
                      dir="ltr"
                      className={widthError ? "pvc-input input--error" : "pvc-input"}
                      inputMode="decimal"
                      autoComplete="off"
                      value={configState.width.raw}
                      aria-invalid={Boolean(widthError)}
                      aria-describedby={widthError ? "pvc-width-error" : undefined}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => page.onDimensionInput("widthCm", event.currentTarget.value)}
                      onBlur={() => void page.onDimensionBlur("widthCm")}
                    />
                    <FieldErrorText id="pvc-width-error" message={widthError} />
                  </label>
                  <label className="pvc-field" htmlFor="pvc-height">
                    <span>גובה בס״מ</span>
                    <input
                      id="pvc-height"
                      dir="ltr"
                      className={heightError ? "pvc-input input--error" : "pvc-input"}
                      inputMode="decimal"
                      autoComplete="off"
                      value={configState.height.raw}
                      aria-invalid={Boolean(heightError)}
                      aria-describedby={heightError ? "pvc-height-error" : undefined}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => page.onDimensionInput("heightCm", event.currentTarget.value)}
                      onBlur={() => void page.onDimensionBlur("heightCm")}
                    />
                    <FieldErrorText id="pvc-height-error" message={heightError} />
                  </label>
                </div>
                <div className="pvc-area-summary" aria-live="off">
                  {area !== null ? <span>שטח ליחידה: <bdi>{area.toFixed(3)} מ״ר</bdi></span> : <span>השטח יחושב לאחר הזנת המידות</span>}
                  {totalArea !== null && configState.quantity.value !== null && configState.quantity.value > 1
                    ? <span>סה״כ: <bdi>{totalArea.toFixed(3)} מ״ר</bdi></span>
                    : null}
                </div>
                {availableSizePresets.length > 0 ? (
                  <div className="pvc-size-presets" aria-label="מידות נפוצות">
                    <span>מידות נפוצות:</span>
                    <div>
                      {availableSizePresets.map(preset => {
                        const selected = configState.width.value === preset.width && configState.height.value === preset.height;
                        return (
                          <button
                            type="button"
                            className="pvc-size-preset"
                            aria-pressed={selected}
                            key={`${preset.width}x${preset.height}`}
                            onClick={() => void applySizePreset(preset)}
                          >
                            <strong>{preset.label}</strong>
                            <bdi>{preset.width}×{preset.height}</bdi>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </fieldset>

              <fieldset className="pvc-section">
                <legend>כמות</legend>
                <div className="pvc-quantity-row">
                  <button type="button" className="pvc-stepper-button" aria-label="הפחתת כמות" onClick={() => void page.onQuantityStep(-1)}>−</button>
                  <input
                    id="pvc-quantity"
                    dir="ltr"
                    className={quantityError ? "pvc-input pvc-quantity-input input--error" : "pvc-input pvc-quantity-input"}
                    inputMode="numeric"
                    autoComplete="off"
                    value={configState.quantity.raw}
                    aria-label="כמות"
                    aria-invalid={Boolean(quantityError)}
                    aria-describedby={quantityError ? "pvc-quantity-error" : undefined}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => page.onQuantityInput(event.currentTarget.value)}
                    onBlur={() => void page.onQuantityBlur()}
                  />
                  <button type="button" className="pvc-stepper-button" aria-label="הגדלת כמות" onClick={() => void page.onQuantityStep(1)}>+</button>
                </div>
                <FieldErrorText id="pvc-quantity-error" message={quantityError} />
                {configState.validation.clientErrors.quantity?.code === "QUANTITY_EXCEEDS_SELF_SERVE" && props.onBusinessQuote ? (
                  <button className="pvc-link-button" type="button" onClick={props.onBusinessQuote}>לקבלת הצעת מחיר לכמות גדולה</button>
                ) : null}
              </fieldset>

              <fieldset className="pvc-section" id="pvc-material-group" tabIndex={-1}>
                <legend>חומר</legend>
                <div className="pvc-choice-list">
                  {schema.materials.map(material => {
                    const presentation = materialById.get(material.id);
                    return (
                      <label className="choice-control choice-control--radio" key={material.id} data-disabled={!material.available || undefined}>
                        <input
                          className="choice-control__input"
                          type="radio"
                          name="pvc-material"
                          value={material.id}
                          checked={configState.materialId === material.id}
                          disabled={!material.available}
                          onChange={() => void page.onMaterialChange(material.id)}
                        />
                        <span className="choice-control__indicator" aria-hidden="true" />
                        <span className="choice-control__copy">
                          <span className="choice-control__label">{presentation?.label ?? material.id}</span>
                          {presentation?.description ? <span className="choice-control__description">{presentation.description}</span> : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <FieldErrorText id="pvc-material-error" message={materialError} />
              </fieldset>

              <fieldset className="pvc-section" id="pvc-addon-group" tabIndex={-1}>
                <legend>תוספות</legend>
                {schema.addons.length === 0 ? <p className="pvc-muted">אין תוספות זמינות למוצר זה כרגע.</p> : (
                  <div className="pvc-choice-list">
                    {schema.addons.map(addon => {
                      const presentation = addonById.get(addon.id);
                      const rushRequired = addon.id === "rush" && configState.requestedDate !== null &&
                        configState.requestedDate < addIsraeliProductionDays(todayInIsrael(), PVC_STANDARD_PRODUCTION_DAYS);
                      return (
                        <label className="choice-control choice-control--checkbox" key={addon.id} data-disabled={!addon.available || rushRequired || undefined}>
                          <input
                            className="choice-control__input"
                            type="checkbox"
                            checked={configState.addonIds.includes(addon.id)}
                            disabled={!addon.available || rushRequired}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => void page.onAddonChange(addon.id, event.currentTarget.checked)}
                          />
                          <span className="choice-control__indicator" aria-hidden="true" />
                          <span className="choice-control__copy">
                            <span className="choice-control__label">{presentation?.label ?? addon.id}</span>
                            {presentation?.description ? <span className="choice-control__description">{presentation.description}</span> : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <FieldErrorText id="pvc-addon-error" message={addonError} />
              </fieldset>

              <fieldset className="pvc-section" id="pvc-artwork-section" tabIndex={-1}>
                <legend>קובץ גרפיקה</legend>
                {props.artworkDesigner}
                <div className="pvc-choice-list">
                  {product.artworkOptions.map(option => (
                    <label className="choice-control choice-control--radio" key={option.mode} data-disabled={!option.available || undefined}>
                      <input
                        className="choice-control__input"
                        type="radio"
                        name="pvc-artwork-mode"
                        checked={artworkState.mode === option.mode}
                        disabled={!option.available}
                        onChange={() => void chooseArtworkMode(option.mode)}
                      />
                      <span className="choice-control__indicator" aria-hidden="true" />
                      <span className="choice-control__copy">
                        <span className="choice-control__label">{option.label}</span>
                        {option.description ? <span className="choice-control__description">{option.description}</span> : null}
                      </span>
                    </label>
                  ))}
                </div>

                {artworkState.mode === "upload-now" ? (
                  <div className="pvc-artwork-action">
                    <input ref={fileInputRef} className="pvc-visually-hidden" type="file" onChange={onFileSelected} />
                    {!artworkState.file ? (
                      <button className="pvc-button pvc-button--secondary" type="button" onClick={() => fileInputRef.current?.click()} disabled={artworkState.directUpload.status === "uploading"}>בחירת קובץ</button>
                    ) : null}
                    {artworkState.directUpload.status === "uploading" ? (
                      <div className="pvc-upload-progress">
                        <span>{artworkState.directUpload.progress === null ? "מעלה קובץ…" : `מעלה קובץ… ${Math.round(artworkState.directUpload.progress * 100)}%`}</span>
                        <button className="pvc-link-button" type="button" onClick={() => page.onCancelArtworkUpload()}>ביטול</button>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {artworkState.mode === "request-upload-link" ? (
                  <div className="pvc-artwork-action">
                    {!artworkState.uploadSession ? (
                      <button className="pvc-button pvc-button--secondary" type="button" onClick={() => void page.onCreateUploadLink().catch(() => setLiveMessage("לא הצלחנו ליצור קישור העלאה כרגע."))}>יצירת קישור להעלאה מהמחשב</button>
                    ) : (
                      <div className="pvc-upload-link-card">
                        <div>
                          <strong>קישור העלאה פעיל</strong>
                          <span>תקף עד <bdi>{new Date(artworkState.uploadSession.expiresAt).toLocaleString("he-IL")}</bdi></span>
                        </div>
                        {artworkState.uploadSession.uploadUrl ? (
                          <div className="pvc-inline-actions">
                            <a className="pvc-button pvc-button--secondary" href={artworkState.uploadSession.uploadUrl} target="_blank" rel="noreferrer">פתיחת הקישור</a>
                            <button
                              className="pvc-link-button"
                              type="button"
                              onClick={() => void navigator.clipboard?.writeText(artworkState.uploadSession?.uploadUrl ?? "").then(() => setLiveMessage("הקישור הועתק."))}
                            >העתקת קישור</button>
                          </div>
                        ) : null}
                        {artworkState.uploadSession.status === "manual-check" ? (
                          <button className="pvc-button pvc-button--secondary" type="button" onClick={() => void page.onCheckUploadSession()}>בדיקת סטטוס</button>
                        ) : null}
                        {(artworkState.uploadSession.status === "expired" || artworkState.uploadSession.status === "failed") ? (
                          <button className="pvc-button pvc-button--secondary" type="button" onClick={() => void page.onCreateUploadLink().catch(() => setLiveMessage("לא הצלחנו ליצור קישור העלאה כרגע."))}>יצירת קישור חדש</button>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : null}

                {artworkState.file ? (
                  <div className="pvc-file-card" data-accepted-artwork-file>
                    <div>
                      <span className="badge badge--success">התקבל</span>{" "}
                      <bdi className="ltr-isolate">{artworkState.file.filename}</bdi>
                    </div>
                    <div className="pvc-inline-actions">
                      {artworkState.mode === "upload-now" ? (
                        <button className="pvc-button pvc-button--secondary" type="button" onClick={() => fileInputRef.current?.click()}>החלפת קובץ</button>
                      ) : null}
                      <button className="pvc-button pvc-button--danger-ghost" type="button" onClick={() => setDialog({ type: "remove-file" })}>הסרת קובץ</button>
                    </div>
                  </div>
                ) : null}

                {artworkMessage ? <p className={artworkState.file ? "pvc-status pvc-status--success" : "pvc-status"}>{artworkMessage}</p> : null}
                {artworkState.assessment.status === "checking" ? <p className="pvc-muted">בודקים את התאמת הקובץ למידה…</p> : null}
                {artworkState.assessment.status === "ready" && artworkState.assessment.warnings.length > 0 ? (
                  <div className="pvc-warning-list" aria-label="אזהרות קובץ">
                    {artworkState.assessment.warnings.map(warning => (
                      <p className="pvc-notice pvc-notice--warning" key={warning.code}>{warning.message}</p>
                    ))}
                    <p className="pvc-muted">האזהרות אינן חוסמות את ההזמנה.</p>
                  </div>
                ) : null}
              </fieldset>

              <fieldset className="pvc-section">
                <legend id="pvc-requested-date-label">תאריך רצוי</legend>
                <p id="pvc-requested-date-help" className="pvc-help">מועד רגיל: החל מ־<bdi>{formatDate(addIsraeliProductionDays(todayInIsrael(), PVC_STANDARD_PRODUCTION_DAYS))}</bdi>. תאריך מוקדם יותר מחייב ייצור דחוף בתוספת 50 ₪. שבתות וחגים אינם נכללים בספירה, וניתן לבחור תאריך עתידי בלבד.</p>
                {props.renderDateField ? props.renderDateField({
                  id: "pvc-requested-date",
                  value: configState.requestedDate,
                  min: addIsraeliProductionDays(todayInIsrael(), 1),
                  ariaInvalid: Boolean(dateError),
                  ariaLabelledBy: "pvc-requested-date-label",
                  describedBy: dateError
                    ? "pvc-requested-date-help pvc-date-error"
                    : "pvc-requested-date-help",
                  onChange: value => void page.onDateChange(value),
                  onBlur: () => undefined,
                }) : (
                  <input
                    id="pvc-requested-date"
                    className={dateError ? "pvc-input input--error" : "pvc-input"}
                    type="date"
                    min={addIsraeliProductionDays(todayInIsrael(), 1)}
                    value={configState.requestedDate ?? ""}
                    aria-invalid={Boolean(dateError)}
                    aria-labelledby="pvc-requested-date-label"
                    aria-describedby={
                      dateError
                        ? "pvc-requested-date-help pvc-date-error"
                        : "pvc-requested-date-help"
                    }
                    onChange={(event: ChangeEvent<HTMLInputElement>) => void page.onDateChange(event.currentTarget.value || null)}
                  />
                )}
                <FieldErrorText id="pvc-date-error" message={dateError} />
                {configState.requestedDate && configState.addonIds.includes("rush") ? (
                  <p className="pvc-production-estimate pvc-production-estimate--rush">ייצור דחוף: תוספת של 50 ₪ נכללת במחיר שלהלן.</p>
                ) : null}
                {quote ? (
                  <div className="pvc-production-card">
                    <span>מועד משוער: <strong><bdi>{formatDate(quote.productionEstimate.earliestReadyDate)}</bdi></strong></span>
                    {quote.productionEstimate.selectedDateAvailable === false ? <span className="pvc-field-error">התאריך שבחרתם אינו זמין.</span> : null}
                  </div>
                ) : null}
              </fieldset>

              {product.proofRequired ? (
                <div className="pvc-proof-notice">במוצר זה תקבלו הגהה לאישור לפני הייצור.</div>
              ) : null}
            </section>
          </main>

          <aside className="pvc-layout-summary" aria-label="סיכום הזמנה">
            <div className="pvc-order-summary" id="pvc-price-summary" tabIndex={-1}>
              <h2>סיכום ההזמנה</h2>
              <dl className="pvc-configuration-summary">
                <div><dt>מידה</dt><dd>{configState.width.value !== null && configState.height.value !== null ? <bdi>{configState.width.value}×{configState.height.value} ס״מ</bdi> : "—"}</dd></div>
                <div><dt>כמות</dt><dd><bdi>{configState.quantity.value ?? "—"}</bdi></dd></div>
                <div><dt>חומר</dt><dd>{configState.materialId ? (materialById.get(configState.materialId)?.label ?? configState.materialId) : "—"}</dd></div>
              </dl>
              <div className="pvc-summary-price" aria-busy={quoteBusy || undefined}>
                <span>סה״כ</span>
                <PriceValue pricingState={pricingState} onRetry={() => void page.retryPricing()} />
              </div>
              {quote ? <PriceDetails quote={quote} rushSelected={configState.addonIds.includes("rush")} /> : null}

              <button
                className="pvc-button pvc-button--primary pvc-add-button"
                type="button"
                disabled={primaryActionDisabled}
                data-primary-action={priceChangeNeedsRetry ? "retry-price" : "submit"}
                onClick={() => void onPrimaryAction()}
              >
                {primaryActionLabel}
              </button>
            </div>
          </aside>
        </div>
      </div>

      <div className="sticky-bar pvc-mobile-purchase" data-pvc-page-background role="region" aria-label="הוספה לסל">
        <button className="pvc-mobile-price-trigger" type="button" onClick={() => setPriceSheetOpen(true)} aria-expanded={priceSheetOpen} aria-busy={quoteBusy || undefined}>
          <PriceValue pricingState={pricingState} compact onRetry={() => void page.retryPricing()} />
          <span className="pvc-mobile-price-details-label">פרטי מחיר</span>
        </button>
        <button
          className="pvc-button pvc-button--primary"
          type="button"
          disabled={primaryActionDisabled}
          data-primary-action={priceChangeNeedsRetry ? "retry-price" : "submit"}
          onClick={() => void onPrimaryAction()}
        >{primaryActionLabel}</button>
      </div>

      <div className="pvc-live-region" aria-live="polite" aria-atomic="true">{liveMessage}</div>

      <AccessibleSurface
        type="modal"
        open={dialog !== null}
        title={dialog?.type === "remove-file" ? "הסרת הקובץ" : "שינוי אופן מסירת הקובץ"}
        onClose={() => setDialog(null)}
        footer={dialog ? (
          <>
            <button className="pvc-button pvc-button--secondary" type="button" onClick={() => setDialog(null)}>ביטול</button>
            <button className={dialog.type === "remove-file" ? "pvc-button pvc-button--danger" : "pvc-button pvc-button--primary"} type="button" onClick={() => void confirmDialog()}>
              {dialog.type === "remove-file" ? "הסר קובץ" : "המשך"}
            </button>
          </>
        ) : undefined}
      >
        {dialog?.type === "remove-file" ? (
          <p>הקובץ שהעלית יוסר מההזמנה. פעולה זו אינה משפיעה על שאר הגדרות השלט.</p>
        ) : dialog?.type === "mode" && dialog.value.reason === "DETACH_ACCEPTED_FILE" ? (
          <p>כבר משויך קובץ מאושר. מעבר לאפשרות אחרת ינתק אותו מהעבודה.</p>
        ) : (
          <p>קיים קישור העלאה פעיל. מעבר לאפשרות אחרת יפסיק להמתין להעלאה דרך הקישור.</p>
        )}
      </AccessibleSurface>

      <AccessibleSurface
        type="modal"
        open={Boolean(cartOwnership)}
        title="בחירת סל"
        onClose={() => undefined}
        dismissible={false}
      >
        {cartOwnership ? (
          <CartChoice
            guestCart={cartOwnership.guestCart}
            accountCart={cartOwnership.accountCart}
            busy={orderState.status === "resolving-cart-ownership"}
            onChoose={choice => void onResolveCart(choice)}
          />
        ) : null}
      </AccessibleSurface>

      <AccessibleSurface
        type="sheet"
        open={priceSheetOpen}
        title="פירוט מחיר"
        onClose={() => setPriceSheetOpen(false)}
        footer={(
          <button
            className="pvc-button pvc-button--primary pvc-add-button"
            type="button"
            disabled={primaryActionDisabled}
            data-primary-action={priceChangeNeedsRetry ? "retry-price" : "submit"}
            onClick={() => void onPrimaryAction()}
          >{primaryActionLabel}</button>
        )}
      >
        <div className="pvc-sheet-price" aria-busy={quoteBusy || undefined}>
          <PriceValue pricingState={pricingState} onRetry={() => void page.retryPricing()} />
          {quote ? <PriceDetails quote={quote} rushSelected={configState.addonIds.includes("rush")} /> : null}
        </div>
      </AccessibleSurface>
    </>
  );
}
